import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, BellOff, BellRing } from "lucide-react";

const toKey = (b64) => { const pad = "=".repeat((4 - (b64.length % 4)) % 4); const raw = atob((b64 + pad).replace(/-/g, "+").replace(/_/g, "/")); return Uint8Array.from([...raw].map((c) => c.charCodeAt(0))); };
const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

export function usePush() {
  const [state, setState] = useState(supported ? Notification.permission : "unsupported");
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (supported) navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then(setSub).catch(() => {}); }, []);
  const enable = async () => {
    if (!supported) { toast.error("Push isn't supported in this browser. On iPhone, add the app to Home Screen first."); return; }
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setState(perm);
      if (perm !== "granted") { toast.error("Notifications were blocked. Allow them in browser settings."); return; }
      const reg = await navigator.serviceWorker.ready;
      const { data } = await api.get("/push/vapid-public-key");
      const s = (await reg.pushManager.getSubscription()) || (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: toKey(data.key) }));
      await api.post("/push/subscribe", { subscription: s.toJSON(), device: navigator.userAgent.slice(0, 80) });
      setSub(s);
      await api.post("/push/test");
      toast.success("Alerts enabled on this device");
    } catch (e) { toast.error("Couldn't enable alerts: " + (e.message || "unknown error")); } finally { setBusy(false); }
  };
  const disable = async () => {
    if (!sub) return;
    setBusy(true);
    try { await api.delete(`/push/subscribe?endpoint=${encodeURIComponent(sub.endpoint)}`); await sub.unsubscribe(); setSub(null); toast.success("Alerts turned off on this device"); }
    finally { setBusy(false); }
  };
  return { supported, state, enabled: !!sub && state === "granted", enable, disable, busy };
}

export function PushToggle({ compact = false }) {
  const p = usePush();
  if (!p.supported) return null;
  const Icon = p.enabled ? BellRing : state(p) === "denied" ? BellOff : Bell;
  const label = p.enabled ? "Alerts on" : "Enable alerts";
  if (compact) return <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={p.enabled ? p.disable : p.enable} disabled={p.busy} data-testid="push-toggle-compact"><Icon className="h-3.5 w-3.5 mr-1" /> {label}</Button>;
  return (
    <div className="px-3 pb-2">
      <Button variant={p.enabled ? "ghost" : "outline"} className={`w-full justify-start rounded-xl ${p.enabled ? "text-emerald-600 dark:text-emerald-400" : "border-primary/40 text-primary"}`} onClick={p.enabled ? p.disable : p.enable} disabled={p.busy} data-testid="push-toggle">
        <Icon className="h-4 w-4 mr-2" /> {p.busy ? "Please wait…" : label}
      </Button>
    </div>
  );
}
const state = (p) => p.state;
